import { WFCreateStep } from "@/types/WorkflowCreate/workflowCreate";
import { WorkflowTask } from "@/types/WorkflowCreate/workflowUpdateV2";
import { getFiltersDifference } from "./getFiltersDifference";
import { getOptionsDifference } from "./getOptionsDifference";
import { WorkflowStepType } from "@/constants/workflows/workflowCreate";

type WorkflowTaskWithType = WorkflowTask & { stepType?: WorkflowStepType };

const isTransformationTask = (stepType?: WorkflowStepType, appId?: string | null) =>
  stepType === WorkflowStepType.TRANSFORMATION || appId === null || appId === undefined;

const getTaskKey = (
  appId: string | null | undefined,
  order: number,
  stepType?: WorkflowStepType
) =>
  isTransformationTask(stepType, appId)
    ? `${WorkflowStepType.TRANSFORMATION}|${order}`
    : `${appId}|${order}`;

const buildTransformationTaskPayload = (
  payload: Pick<WorkflowTask, "action" | "subject" | "order"> &
    Partial<Pick<WorkflowTask, "id" | "remove">>
): WorkflowTask => ({
  action: payload.action,
  subject: payload.subject,
  order: payload.order,
  id: payload.id,
  appId: null,
  stepType: WorkflowStepType.TRANSFORMATION,
  remove: payload.remove,
  workflowTaskCredentials: [],
  workflowTaskFilters: [],
  workflowTaskOptions: []
});

export function buildUpdatedWorkflowTasks(
  oldTasks: WorkflowTask[],
  newTasks: WFCreateStep[]
): WorkflowTask[] {
  const workflowTasks: WorkflowTask[] = [];

  const oldMap = new Map(
    oldTasks.map(task => [
      getTaskKey((task as WorkflowTaskWithType).appId, task.order, (task as WorkflowTaskWithType).stepType),
      task as WorkflowTaskWithType
    ])
  );
  const newMap = new Map(
    newTasks.map(task => [
      getTaskKey(task.id, task.order, task.stepType),
      task
    ])
  );

  // Build from new tasks (base)
  for (const newTask of newTasks) {
    const key = getTaskKey(newTask.id, newTask.order, newTask.stepType);
    const oldTask = oldMap.get(key) as WorkflowTaskWithType | undefined;

    if (oldTask) {
      if (newTask.stepType === WorkflowStepType.TRANSFORMATION) {
        workflowTasks.push(
          buildTransformationTaskPayload({
            id: oldTask.id,
            order: newTask.order,
            action: newTask.action,
            subject: newTask.subject
          })
        );
      } else {
        // Existing task -> keep old id
        workflowTasks.push({
          action: newTask.action,
          appId: newTask.id,
          id: oldTask.id,
          order: newTask.order, // temp, will normalize later
          subject: newTask.subject,
          stepType: WorkflowStepType.APP_BASED,
          workflowTaskCredentials: [
            {
              credentialId: newTask.credentialId,
              id: oldTask.workflowTaskCredentials[0]?.id
            }
          ],
          workflowTaskFilters: getFiltersDifference(
            oldTask.workflowTaskFilters,
            newTask.filters
          ),
          workflowTaskOptions: getOptionsDifference(
            oldTask.workflowTaskOptions,
            newTask.options
          )
        });
      }
    } else {
      if (newTask.stepType === WorkflowStepType.TRANSFORMATION) {
        workflowTasks.push(
          buildTransformationTaskPayload({
            order: newTask.order,
            action: newTask.action,
            subject: newTask.subject
          })
        );
      } else {
        // New task -> no id
        workflowTasks.push({
          action: newTask.action,
          appId: newTask.id,
          order: newTask.order,
          subject: newTask.subject,
          stepType: WorkflowStepType.APP_BASED,
          workflowTaskCredentials: [
            { credentialId: newTask.credentialId }
          ],
          workflowTaskFilters: newTask.filters,
          workflowTaskOptions: newTask.options
        });
      }
    }
  }

  // Add removed tasks at the end (considering both appId and order)
  for (const oldTask of oldTasks) {
    const oldKey = getTaskKey((oldTask as WorkflowTaskWithType).appId, oldTask.order, (oldTask as WorkflowTaskWithType).stepType);
    if (!newMap.has(oldKey)) {
      workflowTasks.push({
        action: oldTask.action,
        appId: oldTask.appId,
        id: oldTask.id,
        order: oldTask.order, // keep original
        subject: oldTask.subject,
        remove: true,
        stepType: (oldTask as WorkflowTaskWithType).stepType,
        workflowTaskCredentials: oldTask.workflowTaskCredentials.map(c => ({
          credentialId: c.credentialId,
          id: c.id,
          remove: true
        })),
        workflowTaskFilters: oldTask.workflowTaskFilters.map(f => ({
          id: f.id,
          filter: f.filter,
          filterValue: f.filterValue,
          remove: true
        })),
        workflowTaskOptions: oldTask.workflowTaskOptions.map(o => ({
          id: o.id,
          option: o.option,
          optionValue: o.optionValue,
          remove: true
        }))
      });
    }
  }

  // Normalize order for active tasks (non-removed)
  let normalizedOrder = 0;
  workflowTasks
    .filter(t => !t.remove)
    .sort((a, b) => a.order - b.order)
    .forEach(task => {
      task.order = normalizedOrder++;
    });

  // Keep removed tasks at the end, in original order
  const finalTasks = [
    ...workflowTasks.filter(t => !t.remove),
    ...workflowTasks.filter(t => t.remove)
  ];

  return finalTasks;
}
